import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import { ReactComponent as CloseIcon } from "../../Assets/iconsv2/generic/closeBtn.svg";
import { useConferencesService } from "../../Context/ConferenceContext";
import { useNotify } from "../../Helpers/Notifications";
import SpinnerComponent from "../GenericComponents/SpinnerComponent";
import OrganizerPresenterPollView from "./OrganizerPresenterPollView";
import InviteesPollView from "./InviteesPollView";
import { deepClone, normalizeText } from "../../Helpers/pollHelpers";
import {
  MAX_POLLS,
  MAX_OPTIONS,
  MIN_OPTIONS,
  QUESTION_MIN_LENGTH,
  POLL_STATUS_DRAFT,
  POLL_STATUS_LAUNCHED,
  POLL_STATUS_ENDED,
  ANSWER_TYPE_SINGLE,
  ANSWER_TYPE_MULTIPLE,
} from "../../Constants/constantValues";

const PollPanel = ({
  conferenceId,
  isOrganizer = false,
  isPresenter = false,
  onClose,
  addObserver,
  removeObserver,
  fullWidth = false,
}) => {
  const { t } = useTranslate();
  const { notifyError, notifySuccess } = useNotify();
  const { 
    getConferencePolls,
    createPollQuestion,
    createPollOption,
    updatePollQuestion,
    updatePollOption,
    launchPoll,
    endPoll,
    deletePollQuestion,
    deletePollOption,
    reorderPollQuestions,
    reorderPollOptions,
    submitPollResponse,
  } = useConferencesService();

  const [polls, setPolls] = useState([]);
  const [originalPolls, setOriginalPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPollId, setExpandedPollId] = useState(null);
  const [savingPollId, setSavingPollId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canManagePolls = isOrganizer || isPresenter;

  useEffect(() => {
    if (conferenceId) {
      loadPolls();
    }
  }, [conferenceId]);

  const loadPolls = () => {
    setLoading(true);
    getConferencePolls(
      conferenceId,
      (response) => {
        const loadedPolls = response?.data?.polls || [];
        setPolls(loadedPolls);
        setOriginalPolls(deepClone(loadedPolls));
        setLoading(false);
        if (!expandedPollId) { 
          const active = loadedPolls.find(p => p.status === POLL_STATUS_LAUNCHED);
          if (active) {
            setExpandedPollId(active.id);
          } else if (loadedPolls.length > 0) {
            setExpandedPollId(loadedPolls[0].id);
          }
        }
      },
      (error) => {
        notifyError(t("general.something_wrong"));
        setLoading(false);
      }
    );
  };

  const isPollModified = (pollId) => {
    const currentPoll = polls.find(p => p.id === pollId);
    if (!currentPoll) return false;

    const originalPoll = originalPolls.find(p => p.id === pollId);

    if (String(pollId).startsWith("temp-")) {
      if (!originalPoll) {
        const hasQuestion = normalizeText(currentPoll.question_text).length >= QUESTION_MIN_LENGTH;
        const validOptions = currentPoll.options.filter(
          opt => normalizeText(opt.option_text).length > 0
        );
        return hasQuestion && validOptions.length >= MIN_OPTIONS;
      }
    }

    if (!originalPoll) return true;

    if (normalizeText(currentPoll.question_text) !== normalizeText(originalPoll.question_text)) {
      return true;
    }

    if (currentPoll.answer_type !== originalPoll.answer_type) {
      return true;
    }

    if (currentPoll.options.length !== originalPoll.options.length) {
      return true;
    }

    for (const currentOpt of currentPoll.options) {
      const originalOpt = originalPoll.options.find(o => o.id === currentOpt.id);
      if (!originalOpt) return true;
      if (normalizeText(currentOpt.option_text) !== normalizeText(originalOpt.option_text)) {
        return true;
      }
    }

    return false;
  };

  const handleAddPoll = () => {
    if (polls.length >= MAX_POLLS) {
      notifyError(t("conferences.max_polls_reached", { max: MAX_POLLS }));
      return;
    }

    const timestamp = Date.now();
    const newPoll = {
      id: `temp-${timestamp}`,
      question_text: "",
      answer_type: ANSWER_TYPE_SINGLE,
      status: POLL_STATUS_DRAFT,
      options: [
        { id: `temp-opt-${timestamp}-1`, option_text: "", position: 0 },
        { id: `temp-opt-${timestamp}-2`, option_text: "", position: 1 },
      ],
      _isNew: true,
    };

    setPolls([...polls, newPoll]);
    setExpandedPollId(newPoll.id);
  };

    const handleSavePoll = (pollId) => {
    const poll = polls.find(p => p.id === pollId);
    if (!poll) return;

    if (!poll.question_text || poll.question_text.trim().length < QUESTION_MIN_LENGTH) {
      notifyError(t("conferences.question_text_required"));
      return;
    }

    const validOptions = poll.options.filter(opt => opt.option_text.trim().length > 0);
    if (validOptions.length < MIN_OPTIONS) {
      notifyError(t("conferences.min_options_required", { min: MIN_OPTIONS }));
      return;
    }

    setSavingPollId(pollId);

    const isTempPoll = String(pollId).startsWith("temp-");

    if (isTempPoll) {
      createPollQuestion(
        conferenceId,
        poll.question_text.trim(),
        poll.answer_type,
        polls.length,
        (questionResponse) => {
          const newQuestion = questionResponse?.data?.poll;
          if (!newQuestion?.id) {
          notifyError(t("general.something_wrong"));
          setSavingPollId(null);
          setExpandedPollId(null);
          return;
        }

        const optionPromises = validOptions.map((opt, index) => 
          new Promise((resolve, reject) => {
            createPollOption(
              newQuestion.id,
              opt.option_text.trim(),
              index,
              resolve,
              reject
            );
          })
        );

        Promise.all(optionPromises)
          .then(() => {
            notifySuccess(t("conferences.poll_saved"));
            setSavingPollId(null);
            loadPolls();
          })
          .catch(() => {
            notifyError(t("general.something_wrong"));
            setSavingPollId(null);
          });
      },
      (error) => {
        notifyError(t("general.something_wrong"));
        setSavingPollId(null);
      }
    );
  } else {
    updatePollQuestion(
      pollId,
      poll.question_text.trim(),
      poll.answer_type,
      (questionResponse) => {
        const originalPoll = originalPolls.find(p => p.id === pollId);
        const currentOptionIds = poll.options.map(opt => opt.id);
        const deletedOptions = originalPoll?.options.filter(
          opt => !String(opt.id).startsWith("temp-") && !currentOptionIds.includes(opt.id)
        ) || [];

        const optionsToCreate = poll.options.filter(opt => String(opt.id).startsWith("temp-"));
        const optionsToUpdate = poll.options.filter(opt => !String(opt.id).startsWith("temp-"));

        const allPromises = [];

        deletedOptions.forEach((opt) => {
          allPromises.push(
            new Promise((resolve, reject) => {
              deletePollOption(
                opt.id,
                resolve,
                reject
              );
            })
          );
        });

        optionsToCreate.forEach((opt) => {
          allPromises.push(
            new Promise((resolve, reject) => {
              createPollOption(
                pollId,
                opt.option_text.trim(),
                poll.options.indexOf(opt),
                resolve,
                reject
              );
            })
          );
        });

        optionsToUpdate.forEach((opt) => {
          allPromises.push(
            new Promise((resolve, reject) => {
              updatePollOption(
                opt.id,
                opt.option_text.trim(),
                poll.options.indexOf(opt),
                resolve,
                reject
              );
            })
          );
        });

        Promise.all(allPromises)
          .then(() => {
            notifySuccess(t("conferences.poll_saved"));
            setSavingPollId(null);
            setExpandedPollId(null);
            loadPolls();
          })
          .catch(() => {
            notifyError(t("general.something_wrong"));
            setSavingPollId(null);
          });
      },
      (error) => {
        notifyError(t("general.something_wrong"));
        setSavingPollId(null);
      }
    );
  }
};

  const handleLaunchPoll = (pollId) => {
    launchPoll(
      pollId,
      (response) => {
        notifySuccess(t("conferences.poll_launched"));
        loadPolls();
      },
      (error) => {
        notifyError(t("general.something_wrong"));
      }
    );
  };

  const handleEndPoll = (pollId) => {
    endPoll(
      pollId,
      (response) => {
        notifySuccess(t("conferences.poll_ended"));
        loadPolls();
      },
      (error) => {
        notifyError(t("general.something_wrong"));
      }
    );
  };

  const handleDeletePoll = (pollId) => {
    const poll = polls.find(p => p.id === pollId);
    if (!poll) return;

    if (poll.status !== POLL_STATUS_DRAFT) {
      notifyError(t("conferences.cannot_delete_active_poll"));
      return;
    }

    if (String(pollId).startsWith("temp-")) {
      setPolls(prev => prev.filter(p => p.id !== pollId));
      notifySuccess(t("conferences.poll_deleted"));
    } else {
      deletePollQuestion(
        pollId,
        () => {
          notifySuccess(t("conferences.poll_deleted"));
          loadPolls();
        },
        () => notifyError(t("general.something_wrong"))
      );
    }
  };

  const handleUpdateQuestion = (pollId, text) => {
    setPolls(prev => prev.map(p => 
      p.id === pollId ? { ...p, question_text: text } : p
    ));
  };

  const handleUpdateAnswerType = (pollId, type) => {
    setPolls(prev => prev.map(p => 
      p.id === pollId ? { ...p, answer_type: type } : p
    ));
  };

  const handleUpdateOption = (pollId, optionId, text) => {
    setPolls(prev => prev.map(p => {
      if (p.id !== pollId) return p;
      return {
        ...p,
        options: p.options.map(opt => 
          opt.id === optionId ? { ...opt, option_text: text } : opt
        ),
      };
    }));
  };

  const handleAddOption = (pollId) => {
    setPolls(prev => prev.map(p => {
      if (p.id !== pollId) return p;
      if (p.options.length >= MAX_OPTIONS) {
        notifyError(t("conferences.max_options_reached", { max: MAX_OPTIONS }));
        return p;
      }
      const newOption = {
        id: `temp-opt-${Date.now()}`,
        option_text: "",
        position: p.options.length,
      };
      
      return {
        ...p,
        options: [...p.options, newOption],
      };
    }));
  };

  const handleDeleteOption = (pollId, optionId) => {
    const poll = polls.find(p => p.id === pollId);
    if (!poll || poll.options.length <= MIN_OPTIONS) {
      notifyError(t("conferences.min_options_required", { min: MIN_OPTIONS }));
      return;
    }

    setPolls(prev => prev.map(p => {
      if (p.id !== pollId) return p;
      return {
        ...p,
        options: p.options.filter(opt => opt.id !== optionId),
      };
    }));
  };

  const saveReorderedPolls = (reorderedPolls) => {
    const positions = {};
    reorderedPolls.forEach((poll, index) => {
      if (!String(poll.id).startsWith("temp-")) {
        positions[poll.id] = index;
      }
    });
    
    if (Object.keys(positions).length === 0) return;
    
    reorderPollQuestions(
      conferenceId,
      positions,
      () => {
        setOriginalPolls(deepClone(reorderedPolls));
      },
      (error) => {
        notifyError(t("general.something_wrong"));
        loadPolls();
      }
    );
  };

  const saveReorderedOptions = (pollId, reorderedOptions) => {
    if (String(pollId).startsWith("temp-")) return;
    
    const positions = {};
    reorderedOptions.forEach((option, index) => {
      if (!String(option.id).startsWith("temp-")) {
        positions[option.id] = index;
      }
    });
    
    if (Object.keys(positions).length === 0) return;
    
    reorderPollOptions(
      pollId,
      positions,
      () => {
        setOriginalPolls(prev => 
          prev.map(poll => 
            poll.id === pollId 
              ? { ...poll, options: deepClone(reorderedOptions) }
              : poll
          )
        );
      },
      (error) => {
        notifyError(t("general.something_wrong"));
      }
    );
  };

  const handleReorderPolls = (reorderedDraftPolls) => {
    const launchedPolls = polls.filter(p => p.status === POLL_STATUS_LAUNCHED);
    const endedPolls = polls.filter(p => p.status === POLL_STATUS_ENDED);

    const updatedPolls = [
        ...launchedPolls,
        ...reorderedDraftPolls,
        ...endedPolls
    ];
    setPolls(updatedPolls);
    saveReorderedPolls(reorderedDraftPolls);
  };

  const handleReorderOptions = (pollId, newOptions) => {
    setPolls(prev => prev.map(p => {
      if (p.id !== pollId) return p;
      return { ...p, options: newOptions };
    }));
    saveReorderedOptions(pollId, newOptions);
  };

  const handleToggleExpand = (pollId) => {
    setExpandedPollId(expandedPollId === pollId ? null : pollId);
  };

  useEffect(() => {
    if (!conferenceId) return;

    const pollObserver = {
      channelName: "ConferenceCallChannel",
      update: (rawData) => {
        try {
          const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;

          switch (data.message_type) {
            case "poll_created":
              setPolls(prevPolls => {
                const exists = prevPolls.find(p => p.id === data.poll.id);
                if (exists) return prevPolls;
                return [...prevPolls, data.poll];
              });
              setOriginalPolls(prevPolls => {
                const exists = prevPolls.find(p => p.id === data.poll.id);
                if (exists) return prevPolls;
                return [...prevPolls, deepClone(data.poll)];
              });
              break;
            
            case "poll_updated":
              setPolls(prevPolls => 
                prevPolls.map(p => 
                  p.id === data.poll.id 
                    ? { ...p, ...data.poll }
                    : p
                )
              );
              setOriginalPolls(prevPolls => 
                prevPolls.map(p => 
                  p.id === data.poll.id 
                    ? { ...p, ...deepClone(data.poll) }
                    : p
                )
              );
              break;
            
            case "poll_results_updated":
              setPolls(prevPolls => 
                prevPolls.map(p => 
                  p.id === data.poll.id 
                    ? { ...p, ...data.poll }
                    : p
                )
              );
              break;
            
            case "poll_launched":
              setPolls(prevPolls => {
              const exists = prevPolls.find(p => p.id === data.poll.id);
              
              if (exists) {
                return prevPolls.map(p => 
                  p.id === data.poll.id 
                    ? { ...p, ...data.poll }
                    : p
                );
              } else {
                return [...prevPolls, data.poll];
              }
            });
            
            setOriginalPolls(prevPolls => {
              const exists = prevPolls.find(p => p.id === data.poll.id);
              
              if (exists) {
                return prevPolls.map(p => 
                  p.id === data.poll.id 
                    ? { ...p, ...deepClone(data.poll) }
                    : p
                );
              } else {
                return [...prevPolls, deepClone(data.poll)];
              }
            });
              if (!canManagePolls) {
                setExpandedPollId(data.poll.id);
              }
              break;
            
            case "poll_ended":
              setPolls(prevPolls => 
                prevPolls.map(p => 
                  p.id === data.poll.id 
                    ? { ...p, ...data.poll }
                    : p
                )
              );
              setOriginalPolls(prevPolls => 
                prevPolls.map(p => 
                  p.id === data.poll.id 
                    ? { ...p, ...deepClone(data.poll) }
                    : p
                )
              );
              break;
            
            case "poll_deleted":
              setPolls(prevPolls => prevPolls.filter(p => p.id !== data.poll_id));
              setOriginalPolls(prevPolls => prevPolls.filter(p => p.id !== data.poll_id));
              break;
            
            case "polls_reordered":
              setPolls(data.polls);
              setOriginalPolls(deepClone(data.polls));
              break;
            
            default:
              break;
          }
        } catch (error) {
        
        }
      },
    };  

    if (addObserver) {
      addObserver(pollObserver);
    }

    return () => {
      if (removeObserver) {
        removeObserver(pollObserver);
      }
    };
  }, [conferenceId, canManagePolls, addObserver, removeObserver]);

  return (
    <Container $fullWidth={fullWidth}>
      <Header>
        <HeaderTitle>
          {canManagePolls
            ? t("conferences.manage_polls")
            : t("conferences.polls")}
        </HeaderTitle>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </Header>

      <Content>
        {loading && (
          <LoadingWrapper>
            <SpinnerComponent isLoading={true} />
          </LoadingWrapper>
        )}

        {!loading && !canManagePolls && (
          <InviteesPollView
            polls={polls}
            onSubmitVote={(pollId, selectedOptions, onSuccess) => {
              setIsSubmitting(true);
              submitPollResponse(
                pollId,
                selectedOptions,
                () => {
                  notifySuccess(t("conferences.vote_submitted"));
                  onSuccess();
                  setIsSubmitting(false);
                  loadPolls();
                },
                () => {
                  notifyError(t("general.something_wrong"));
                  setIsSubmitting(false);
                }
              );
            }}
            isSubmitting={isSubmitting}
          />
        )}

        {!loading && canManagePolls && (
          <OrganizerPresenterPollView
            polls={polls}
            expandedPollId={expandedPollId}
            savingPollId={savingPollId}
            onToggleExpand={handleToggleExpand}
            onAddPoll={handleAddPoll}
            onSavePoll={handleSavePoll}
            onLaunchPoll={handleLaunchPoll}
            onEndPoll={handleEndPoll}
            onDeletePoll={handleDeletePoll}
            onUpdateQuestion={handleUpdateQuestion}
            onUpdateAnswerType={handleUpdateAnswerType}
            onUpdateOption={handleUpdateOption}
            onAddOption={handleAddOption}
            onDeleteOption={handleDeleteOption}
            onReorderPolls={handleReorderPolls}
            onReorderOptions={handleReorderOptions}
            isPollModified={isPollModified}
            loading={loading}
          />
        )}
      </Content>
    </Container>
  );
};

export default PollPanel;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-width: ${({ $fullWidth }) => $fullWidth ? "0" : "300px"};
  max-width: ${({ $fullWidth }) => $fullWidth ? "none" : "400px"};
  background: ${({ theme }) => theme.callComponent.participantsListBackground};
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 16px;
  background: ${({ theme }) => theme.callComponent.participantsListBackground};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  min-height: 70px;
`;

const HeaderTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  line-height: 22px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  margin-left: auto;
  
  svg {
    height: 28px;
    width: 28px;
    color: ${({ theme }) => theme.menu.selectedOpionBackground};
    stroke: ${({ theme }) => theme.sidebar.borderColor};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  height: 0;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 10px;
`;